import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

interface Shortcut {
  id: string;
  key_code: string;
  action: string;
  description: string | null;
}

const ACTIONS: { value: string; label: string }[] = [
  { value: "search_product", label: "Buscar producto" },
  { value: "search_customer", label: "Buscar cliente" },
  { value: "new_customer", label: "Nuevo cliente" },
  { value: "discount", label: "Aplicar descuento" },
  { value: "change_price", label: "Cambiar precio / mayorista" },
  { value: "remove_item", label: "Eliminar producto" },
  { value: "change_qty", label: "Cambiar cantidad" },
  { value: "suspend_sale", label: "Suspender venta" },
  { value: "resume_sale", label: "Recuperar venta" },
  { value: "new_invoice", label: "Nueva factura" },
  { value: "reprint_last", label: "Reimprimir última factura" },
  { value: "charge", label: "Cobrar" },
  { value: "payment", label: "Método de pago" },
  { value: "clear_sale", label: "Cancelar operación" },
];

const KEY_ORDER = ["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12"];

export default function ShortcutsTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("shortcuts")
        .select("id, key_code, action, description");
      if (error) {
        toast.error("No se pudieron cargar los atajos.");
        return;
      }
      const list = (data ?? []) as Shortcut[];
      list.sort((a, b) => KEY_ORDER.indexOf(a.key_code) - KEY_ORDER.indexOf(b.key_code));
      setShortcuts(list);
    } catch {
      toast.error("No se pudieron cargar los atajos.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = async (shortcut: Shortcut, action: string) => {
    setSavingId(shortcut.id);
    const label = ACTIONS.find((item) => item.value === action)?.label ?? action;
    const { error } = await supabase
      .from("shortcuts")
      .update({ action, description: label, updated_at: new Date().toISOString() })
      .eq("id", shortcut.id);
    setSavingId(null);
    if (error) {
      toast.error("No se pudo actualizar el atajo.");
      return;
    }
    setShortcuts((prev) =>
      prev.map((item) => (item.id === shortcut.id ? { ...item, action, description: label } : item)),
    );
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "configurar",
      entity: "shortcuts",
      entityId: shortcut.id,
      oldValue: { action: shortcut.action },
      newValue: { action },
    });
    toast.success(`Atajo ${shortcut.key_code} actualizado.`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg"></i>
        Cargando atajos...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-background-100 p-4 text-sm text-foreground-700">
        <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-foreground-500">
          <i className="ri-keyboard-line text-base"></i>
        </span>
        <p className="flex-1 leading-snug">
          Personaliza la función de cada tecla <strong>F1–F12</strong> en el Punto de Venta. Los
          cambios se aplican de inmediato en el POS.
        </p>
      </div>

      <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-900">Atajos del POS</h3>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {shortcuts.map((shortcut) => (
            <div
              key={shortcut.id}
              className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3"
            >
              <span className="flex h-10 w-12 shrink-0 items-center justify-center rounded-md border border-background-300 bg-background-100 font-heading text-sm font-bold text-foreground-900">
                {shortcut.key_code}
              </span>
              <select
                value={shortcut.action}
                disabled={savingId === shortcut.id}
                onChange={(event) => handleChange(shortcut, event.target.value)}
                className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:opacity-60"
              >
                {ACTIONS.map((action) => (
                  <option key={action.value} value={action.value}>
                    {action.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}